// Показ денег. Правило 5: деньги показываются одинаково везде, а считаются только на
// сервере. Здесь ТОЛЬКО форматирование — никакой арифметики над суммами.
//
// Пустое и нулевое выглядят по-разному (правило 7): null — «данных нет», прочерк;
// 0 — результат расчёта, «0». Слипание этих двух фактов в один прочерк — дефект,
// по такой ячейке финдиректор решает, доплачивать ли человеку.
//
// ## Копеек нет. Решение владельца 10.09.2026 со слов заказчика
//
// «Копейки не используются нигде, а если используются — округляем, типа не 1454,50, а 1455».
// У заказчика деньги выдают наличными, и копейки в его работе не существуют физически:
// разменять их нечем, а в ведомости они читаются как мусор.
//
// Округление здесь — ВТОРАЯ линия, а не единственная. Первая стоит на сервере: движок считает
// начисления и удержания сразу целыми рублями, а ввод дробных сумм больше не принимается. То
// есть в норме округлять тут нечего — этот код работает только на исторических суммах,
// перенесённых из старых книг заказчика, пока их не перенесли заново.

/** Неразрывный пробел между разрядами: перенос строки внутри суммы разрывает число глазу. */
const NBSP = ' ';
/** Типографский минус, а не дефис: дефис теряется рядом с цифрами в плотной сетке. */
const MINUS = '−';

function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/**
 * Целые копейки → «1 234 568». Отрицательные — «−10 749». null и undefined — «—».
 * Дробное или небезопасное число — ошибка сразу: это не деньги, а поломка на границе.
 */
export function formatKopecks(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (!Number.isSafeInteger(value)) {
    throw new Error(`копейки передаются целым числом, получено ${value}`);
  }
  const abs = Math.abs(value);
  // Половина уходит ВВЕРХ, как и везде в проекте: 1454,50 → 1455. Считается от модуля, а
  // потом возвращается знак, поэтому округление симметрично нулю и «минус половина» уходит
  // вниз ровно на столько же, на сколько «плюс половина» уходит вверх.
  const rubles = Math.floor((abs + 50) / 100);
  return `${value < 0 && rubles !== 0 ? MINUS : ''}${groupDigits(String(rubles))}`;
}

/**
 * Показ ставки: хранится дробной строкой dec(18,6), показывается целым рублём.
 * Округление половины вверх (от нуля), как в Excel клиента: 85 000 за период при норме
 * 558 часов — это «152.329749…», и человек видит 152; «152.5» видится как 153.
 * Считается по строке, без плавающей точки: она теряет копейку на шестом знаке.
 */
export function formatRateWholeRubles(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value);
  if (match === null) {
    throw new Error(`ставка передаётся десятичной строкой, получено «${value}»`);
  }
  const [, sign, integerDigits, fractionDigits] = match;
  let whole = Number(integerDigits);
  const firstFractionDigit = fractionDigits?.charCodeAt(0) ?? 0;
  if (firstFractionDigit >= 0x35 /* «5» */) {
    whole += 1;
  }
  return `${sign === '-' && whole !== 0 ? MINUS : ''}${groupDigits(String(whole))}`;
}

/**
 * Дробная величина, пришедшая СТРОКОЙ, к виду для человека: «152.329749» → «152,329749»,
 * «214.500000» → «214,5».
 *
 * Нужна там, где человек сверяет точное значение с нашим расчётом: ставка в разборе строки
 * ведомости. До 25.08.2026 такое значение печаталось как есть — «152.329749 ₽», — то есть с
 * точкой посреди продукта, где все дробные части отделяются запятой.
 *
 * Хвост нулей срезается: «558,000000 из 558,000000» человек читает как подвох, у него в
 * табеле стоит 558.
 */
export function formatDecimalExact(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value);
  if (match === null) {
    throw new Error(`дробная величина передаётся строкой, получено «${value}»`);
  }
  const sign = match[1] ?? '';
  const integerDigits = match[2] ?? '0';
  const fraction = (match[3] ?? '').replace(/0+$/, '');
  const whole = groupDigits(integerDigits);
  const body = fraction === '' ? whole : `${whole},${fraction}`;
  return `${sign === '-' ? MINUS : ''}${body}`;
}

/**
 * То же число, но со знаком рубля: «1 234 568 ₽».
 *
 * Зачем отдельная функция, когда знак — один символ. Правило `ui-views.md` говорит: знак рубля
 * ставится там, где без него не понять, что это деньги. Решение верное, но принималось оно
 * РУКАМИ на каждом из пятидесяти девяти мест, и результат разъехался внутри одной карточки:
 * на экране «Расход по категориям» одна и та же сумма напечатана дважды — «3 200,00» величиной
 * и «3 200,00 ₽» строкой ниже (кадр `кабинет-расход-по-категориям.png`, 03.09.2026). Человек
 * видит одно число, написанное двумя способами, и первым делом ищет между ними разницу.
 *
 * Теперь выбор остаётся, но он называется вслух: `formatKopecks` — «число», `formatRubles` —
 * «деньги». Забыть склеить знак пробелом или склеить обычным вместо неразрывного больше нельзя.
 *
 * Пробел НЕРАЗРЫВНЫЙ: сумма, перенесённая по строке между числом и знаком, читается как два
 * разных значения.
 */
export function formatRubles(value: number | null | undefined): string {
  const число = formatKopecks(value);
  // Прочерк деньгами не становится: «— ₽» это не «неизвестно», а мусор.
  return число === '—' ? число : `${число}${NBSP}₽`;
}

/**
 * Ставка со знаком рубля: «152 ₽».
 *
 * Отдельно от `formatRubles`, потому что величина другая: ставка приходит дробной строкой и
 * показывается целым рублём (`formatRateWholeRubles`), а не копейками. Склеивать знак руками
 * здесь нельзя ровно по той же причине, что и с суммой: обычный пробел рвёт «1 200 ₽» на
 * «1 200» и «₽» при переносе строки, и человек читает два разных числа вместо одного.
 */
export function formatRateRubles(value: string | null | undefined): string {
  const число = formatRateWholeRubles(value);
  return число === '—' ? число : `${число}${NBSP}₽`;
}

/**
 * Копейки → короткая подпись оси графика: «13,6 млн», «591,8 тыс», «3,2 тыс».
 *
 * Заведён 08.09.2026 вместе с первыми графиками. У AlignUI ту же работу делает
 * `utils/number-formatter.ts` через `Intl.NumberFormat('en-US', {currency: 'USD'})` — то есть
 * доллары и английские сокращения. Копировать это было нельзя: подпись оси у нас обязана
 * читаться теми же словами, что и остальные суммы в кабинете.
 *
 * Только для ОСИ и подписи столбца. Сама величина везде остаётся полной (`formatRubles`):
 * округлённое до сотых тысяч число нельзя ни сверить, ни подписать, а по числам на этом
 * экране принимают решения о выплатах.
 */
export function formatKopecksCompact(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (!Number.isSafeInteger(value)) {
    throw new Error(`копейки передаются целым числом, получено ${value}`);
  }
  const знак = value < 0 ? MINUS : '';
  const рубли = Math.abs(value) / 100;
  if (рубли >= 1_000_000) {
    return `${знак}${округлить(рубли / 1_000_000)}${NBSP}млн`;
  }
  if (рубли >= 1_000) {
    return `${знак}${округлить(рубли / 1_000)}${NBSP}тыс`;
  }
  return `${знак}${groupDigits(String(Math.round(рубли)))}`;
}

/** Один знак после запятой и запятая вместо точки — как во всех остальных суммах. */
function округлить(число: number): string {
  const целое = Math.round(число * 10) / 10;
  return String(целое).replace('.', ',');
}
